/**
 * Network Store - Manages network selection and switching
 */
import { defineStore } from 'pinia';
import {
	appEnvironment,
	switchNetwork,
	getAvailableNetworks,
	type NetworkConfig,
	type AppEnvironment,
} from '@/shared/appEnvironment';

export const useNetworkStore = defineStore('network', {
	// ========================================
	// STATE
	// ========================================
	state: () => ({
		currentNetworkId: appEnvironment.networkId as string,
		availableNetworks: getAvailableNetworks() as NetworkConfig[],
	}),

	// ========================================
	// GETTERS
	// ========================================
	getters: {
		currentNetwork(state): NetworkConfig | undefined {
			return state.availableNetworks.find((n) => n.id === state.currentNetworkId);
		},

		currentEnvironment(): AppEnvironment {
			return appEnvironment;
		},

		networkSymbol(): string {
			return appEnvironment.symbol;
		},

		networkDisplayName(): string {
			return appEnvironment.displayName;
		},

		isMainnet(): boolean {
			return appEnvironment.isMainnet;
		},

		isTestnet(): boolean {
			return appEnvironment.isTestnet;
		},

		isSolana(): boolean {
			return appEnvironment.network === 'SOLANA';
		},

		isFogo(): boolean {
			return appEnvironment.network === 'FOGO';
		},
	},

	// ========================================
	// ACTIONS
	// ========================================
	actions: {
		/**
		 * Change the active network
		 */
		async changeNetwork(networkId: string): Promise<void> {
			if (!this.availableNetworks.find((n) => n.id === networkId)) {
				console.error(`Network ${networkId} is not available`);
				return;
			}

			// Check if switching away from Solana and currently on Solana-only pages
			const targetNetwork = this.availableNetworks.find((n) => n.id === networkId);
			if (!targetNetwork) return;

			const isSwitchingAwayFromSolana = targetNetwork.network !== 'SOLANA';

			if (typeof window !== 'undefined' && isSwitchingAwayFromSolana) {
				const currentPath = window.location.pathname;
				const currentQuery = window.location.search;

				// Define Solana-only routes that should redirect when switching away from Solana
				const solanaOnlyRoutes = [
					'/app/points/pnl',
					'/app/points/fees',
					'/app/points',
					'/app/bridge',
				];

				// Check if on Solana-only pages
				const isOnSolanaOnlyPage = solanaOnlyRoutes.some((route) => currentPath.startsWith(route));

				// Check if on rewards tab in profile (Solana-only)
				const isOnRewardsTab =
					currentPath === '/app/profile' && currentQuery.includes('tab=rewards');

				if (isOnSolanaOnlyPage || isOnRewardsTab) {
					// Save redirect flag to localStorage
					localStorage.setItem('network_switch_redirect', 'true');
				}
			}

			const { useSolanaStore } = await import('./solanaStore');

			// Switch the network
			switchNetwork(networkId);
			this.currentNetworkId = networkId;

			// Update Solana RPC endpoint
			const solanaStore = useSolanaStore();
			const customRpcUrl = localStorage.getItem('settings')
				? JSON.parse(localStorage.getItem('settings') || '{}').customRpcUrl
				: null;
			const rpcUrl = customRpcUrl || appEnvironment.urls.rpc;
			solanaStore.init(rpcUrl);

			// Emit event for other components to react
			if (typeof window !== 'undefined') {
				window.dispatchEvent(
					new CustomEvent('network-changed', {
						detail: { networkId, network: appEnvironment },
					})
				);
			}

			// Reload the page to ensure all components use the new network
			if (typeof window !== 'undefined') {
				window.location.reload();
			}
		},

		/**
		 * Refresh available networks (useful after deployment environment changes)
		 */
		refreshAvailableNetworks(): void {
			this.availableNetworks = getAvailableNetworks();
		},
	},
});
